/**
 * 관리자 페이지 공통 레이아웃
 *
 * 좌측 사이드바(접힘/펼침 토글), 상단 헤더(로그아웃), 하단 푸터로 구성된다.
 * 인증이 필요한 모든 /admin/* 경로는 이 레이아웃 안에서 <Outlet>으로 렌더링된다.
 */
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { authService } from "../services/authService";
import appIcon from "../assets/app_Icon.png";
import ConfirmModal from "../components/ui/ConfirmModal";

const NAV_ITEMS = [
  { to: "/admin", icon: "fas fa-chart-line", label: "대시보드", exact: true },
  {
    to: "/admin/member",
    icon: "fas fa-users",
    label: "회원 관리",
    exact: false,
  },
  {
    to: "/admin/notice",
    icon: "fas fa-bullhorn",
    label: "공지사항",
    exact: false,
  },
  {
    to: "/admin/adver",
    icon: "fas fa-ad",
    label: "광고 관리",
    exact: false,
  },
  {
    to: "/admin/popup",
    icon: "fas fa-window-restore",
    label: "팝업 관리",
    exact: false,
  },
  {
    to: "/admin/market",
    icon: "fas fa-store",
    label: "마켓 관리",
    exact: false,
  },
  {
    to: "/admin/blacklist-rules",
    icon: "fas fa-ban",
    label: "블랙리스트 관리",
    exact: false,
  },
  {
    to: "/admin/blacklist-logs",
    icon: "fas fa-shield-alt",
    label: "차단 로그",
    exact: false,
  },
  {
    to: "/admin/help",
    icon: "fas fa-question-circle",
    label: "사용설명서",
    exact: false,
  },
];

const NAV_ITEMS_UPCOMING: { icon: string; label: string }[] = [];

export default function AdminLayout() {
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (to: string, exact: boolean) =>
    exact ? location.pathname === to : location.pathname.startsWith(to);

  const handleLogout = () => {
    authService.logout();
    navigate("/login");
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <aside
        className={`${sidebarCollapsed ? "w-20" : "w-64"} relative bg-gradient-to-br from-primary via-[#5FA060] to-[#4E8850] text-white transition-all duration-300 shadow-xl`}
      >
        <div className="p-6 flex items-center justify-between border-b border-white/20">
          {!sidebarCollapsed ? (
            <div className="flex items-center">
              <div className="w-8 h-8 bg-white rounded-lg overflow-hidden mr-2 flex-shrink-0">
                <img
                  src={appIcon}
                  alt="배링 로고"
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h1 className="text-2xl font-bold">배링</h1>
                <p className="text-xs text-primary-lighter mt-1">
                  관리자 시스템
                </p>
              </div>
            </div>
          ) : (
            <div className="w-10 h-10 bg-white rounded-lg overflow-hidden mx-auto">
              <img
                src={appIcon}
                alt="배링 로고"
                className="w-full h-full object-cover"
              />
            </div>
          )}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <i
              className={`fas fa-${sidebarCollapsed ? "angle-right" : "angle-left"}`}
            ></i>
          </button>
        </div>

        <nav className="mt-6 px-3">
          {NAV_ITEMS.map(({ to, icon, label, exact }) => (
            <Link
              key={to}
              to={to}
              className={`flex items-center px-4 py-3 mb-2 rounded-lg transition-all ${
                isActive(to, exact)
                  ? "bg-white text-primary shadow-lg"
                  : "text-white hover:bg-white/10"
              }`}
            >
              <i className={`${icon} w-5`}></i>
              {!sidebarCollapsed && <span className="ml-3">{label}</span>}
            </Link>
          ))}

          {/* 준비 중 메뉴 */}
          {!sidebarCollapsed && (
            <p className="text-xs text-white/30 font-medium uppercase tracking-widest px-4 mt-6 mb-2">
              준비 중
            </p>
          )}
          {NAV_ITEMS_UPCOMING.map(({ icon, label }) => (
            <div
              key={label}
              className="flex items-center px-4 py-3 mb-2 rounded-lg text-white/30 cursor-not-allowed select-none"
              title="준비 중인 기능입니다"
            >
              <i className={`${icon} w-5`}></i>
              {!sidebarCollapsed && (
                <span className="ml-3 flex items-center gap-2">
                  {label}
                  <span className="text-[10px] bg-white/10 text-white/40 px-1.5 py-0.5 rounded-full leading-none">
                    준비 중
                  </span>
                </span>
              )}
            </div>
          ))}
        </nav>

        {!sidebarCollapsed && (
          <div className="absolute bottom-6 left-0 right-0 px-6">
            <div className="bg-white/10 rounded-lg p-4 border border-white/20">
              <p className="text-xs text-primary-lighter mb-2">라이프박스</p>
              <p className="text-xs text-white/80">v{__APP_VERSION__}</p>
            </div>
          </div>
        )}
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white border-b border-gray-150 shadow-sm">
          <div className="flex items-center justify-between px-8 py-4">
            <div>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-primary to-[#5FA060] bg-clip-text text-transparent">
                배링 관리 시스템
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Baering Management System
              </p>
            </div>
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2 bg-bg-light px-4 py-2 rounded-lg border border-gray-150">
                <div className="w-8 h-8 bg-gradient-to-br from-primary to-[#5FA060] rounded-full flex items-center justify-center text-white text-sm font-bold">
                  A
                </div>
                <span className="text-gray-600 font-medium">관리자</span>
              </div>
              <button
                onClick={() => setShowLogoutModal(true)}
                className="px-5 py-2.5 bg-gradient-to-r from-text-red to-[#E03030] text-white rounded-lg hover:from-[#E03030] hover:to-[#D02020] transition-all shadow-md hover:shadow-lg flex items-center space-x-2"
              >
                <i className="fas fa-sign-out-alt"></i>
                <span>로그아웃</span>
              </button>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-auto p-8 bg-bg-light">
          <Outlet />
        </main>

        <footer className="bg-white border-t border-gray-150 py-4 text-center">
          <p className="text-sm text-gray-500">
            Copyright © <span className="font-semibold text-primary">배링</span>{" "}
            라이프박스
          </p>
        </footer>
      </div>

      {showLogoutModal && (
        <ConfirmModal
          title="로그아웃을 하시겠습니까?"
          description="로그아웃을 클릭하시면 세션이 종료됩니다."
          confirmLabel="로그아웃"
          onConfirm={handleLogout}
          onCancel={() => setShowLogoutModal(false)}
        />
      )}
    </div>
  );
}
